import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PseudoInputButton,
  PseudoInputIcon,
  PseudoInputLabel,
} from '@1trading/react';
import { getFieldsSearchMatch, normalizeSearchText } from '@1trading/react/lib/search';
import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Secondary text shown smaller and muted after the label, and searched too */
  detail?: string;
}

interface SearchableSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  empty: string;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  className?: string;
  /** Renders as an `InputGroup`'s control, beside the group's add-ons */
  isGroupControl?: boolean;
}

/** An arbitrary value because tailwind-merge doesn't recognise the library's `h-(--input-height)`, so Button's `h-10` would win */
export const SEARCHABLE_TRIGGER_CLASS = 'h-[var(--input-height)] border-input bg-card text-card-foreground';

// What the library's `InputGroupInput` has, so the group draws the border, focus ring and invalid state
const GROUP_CONTROL_CLASS =
  'h-[calc(var(--input-height)-var(--border-width))] min-w-0 flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 aria-expanded:ring-0 aria-invalid:ring-0';

/** Items are told apart by value, as two can share a label, so search runs on these instead */
export const optionSearchTerms = (option: SearchableSelectOption) => [option.label, ...(option.detail ? [option.detail] : [])];

/** Scored as `rankBySearch` scores, as cmdk's own filter would match the value too */
export const optionSearchScore = (_value: string, search: string, keywords: string[] = []) => getFieldsSearchMatch(keywords, normalizeSearchText(search)).score;

export const SearchableOptionContent = ({ option, isSelected }: { option: SearchableSelectOption; isSelected: boolean }) => (
  <>
    <Check className={cn('mr-2 size-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
    <span className="whitespace-nowrap">{option.label}</span>
    {option.detail ? <span className="ml-auto shrink-0 whitespace-nowrap pl-4 text-xs text-muted-foreground">{option.detail}</span> : null}
  </>
);
SearchableOptionContent.displayName = 'SearchableOptionContent';

/**
 * `PseudoInputOptions` with room for a detail line; the list grows past the trigger's width to fit it. Modal, as a popover
 * inside a modal dialog otherwise loses its scrolling to the dialog.
 */
export const SearchableSelect = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  empty,
  disabled,
  'aria-invalid': ariaInvalid,
  className,
  isGroupControl = false,
}: SearchableSelectProps) => {
  const [open, setOpen] = useState(false);
  const label = options.find((option) => option.value === value)?.label;

  return (
    <Popover modal onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <PseudoInputButton
          aria-invalid={ariaInvalid}
          className={cn(SEARCHABLE_TRIGGER_CLASS, isGroupControl && GROUP_CONTROL_CLASS, className)}
          data-slot={isGroupControl ? 'input-group-control' : undefined}
          disabled={disabled}
          id={id}
        >
          <PseudoInputLabel className={cn(!label && 'text-muted-foreground')}>{label ?? placeholder}</PseudoInputLabel>
          <PseudoInputIcon>
            <ChevronDown />
          </PseudoInputIcon>
        </PseudoInputButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
        <Command filter={optionSearchScore}>
          <CommandInput className="text-base" placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty className="py-3 text-center text-base">{empty}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  className="text-base"
                  key={option.value}
                  keywords={optionSearchTerms(option)}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  value={option.value}
                >
                  <SearchableOptionContent isSelected={option.value === value} option={option} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
SearchableSelect.displayName = 'SearchableSelect';
